import { Alert, Modal } from 'antd';
import {
  SysAdminUserListItem,
  useRemoveSysAdminPatientProfileMutation,
  useRemoveSysAdminUserMutation
} from 'api/SysAdminApi';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import './RemoveSysAdminUserModalComponent.css';

export const DELETE_SYS_ADMIN_USER_MODAL_NAME = 'DELETE_SYS_ADMIN_USER_MODAL_NAME';

type AllProps = {
  user: SysAdminUserListItem | null;
};

function RemoveSysAdminUserModalComponent({ user }: AllProps) {
  const dispatch = useAppDispatch();
  const [removeSysAdminUser, { isLoading: isLoadingRemove }] = useRemoveSysAdminUserMutation();
  const [removeSysAdminPatientProfile, { isLoading: isLoadingRemoveProfile }] = useRemoveSysAdminPatientProfileMutation();
  const [removeError, setRemoveError] = useState<string | null>(null);

  if (!user) {
    return null;
  }

  const isProfileOnly = user.recordType === 'patient_profile' || (!user.userId && !!user.patientProfileId);

  const removeTargetName =
    `${user.patientProfile?.firstName || user.firstName || ''} ${user.patientProfile?.lastName || user.lastName || ''}`.trim() ||
    user.email ||
    user.patientProfile?.email ||
    'this profile';

  const handleRemove = async () => {
    try {
      setRemoveError(null);

      if (isProfileOnly) {
        await removeSysAdminPatientProfile({ patientProfileId: user.patientProfileId || user.id }).unwrap();
      } else {
        await removeSysAdminUser({ id: user.userId || user.id }).unwrap();
      }

      handleCloseModal();
    } catch {
      setRemoveError(`Failed to ${isProfileOnly ? 'delete patient profile' : 'remove user'}. Please try again.`);
    }
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  return (
    <Modal
      open={true}
      title={isProfileOnly ? 'Delete Patient Profile' : 'Remove User'}
      onOk={handleRemove}
      onCancel={handleCloseModal}
      okText={isProfileOnly ? 'Delete' : 'Remove'}
      confirmLoading={isLoadingRemove || isLoadingRemoveProfile}
      className="size-sm"
    >
      <div className="remove-sysadmin-user-modal-component">
        <Alert className="message" message="This action cannot be reverted!" type="error" showIcon />
        {removeError ? <Alert className="message" message={removeError} type="error" showIcon /> : null}
        {isProfileOnly ? (
          <>
            Do you want to delete patient profile <strong>{removeTargetName}</strong>?
          </>
        ) : (
          <>
            Do you want to remove <strong>{removeTargetName}</strong>?
          </>
        )}
      </div>
    </Modal>
  );
}

function RemoveSysAdminUserModalComponentWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);
  const user = modalData?.user || null;

  return modalName === DELETE_SYS_ADMIN_USER_MODAL_NAME ? <RemoveSysAdminUserModalComponent user={user} /> : null;
}

export default RemoveSysAdminUserModalComponentWrapper;
